import { getToken, removeToken } from '../utils/auth';

// Use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Request cache to prevent duplicate requests
const requestCache = new Map();

// Helper function to get headers with auth token
const getAuthHeaders = () => {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  return headers;
};

// Helper function to handle API responses and auth errors
const handleResponse = async (response) => {
  if (response.status === 401) {
    // Token expired or invalid, remove it and redirect to login
    removeToken();
    window.location.reload(); // This will trigger the login flow
    throw new Error('Authentication required');
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Network error' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// Helper function to make cached requests (prevents double calls)
const cachedFetch = async (url, options = {}, cacheKey = null) => {
  const key = cacheKey || `${url}_${JSON.stringify(options)}`;
  
  // If same request is already in progress, return the existing promise
  if (requestCache.has(key)) {
    return requestCache.get(key);
  }
  
  // Create new request
  const requestPromise = fetch(url, options)
    .then(handleResponse)
    .finally(() => {
      // Remove from cache after completion
      setTimeout(() => requestCache.delete(key), 100);
    });
  
  // Cache the promise
  requestCache.set(key, requestPromise);
  
  return requestPromise;
};

// PROMPT ENDPOINTS
export const loadDefaultPrompt = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/prompt?type=default`, {
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Error loading default prompt:', error);
    throw error;
  }
};

export const loadWorkingPrompt = async () => {
  return cachedFetch(`${API_BASE_URL}/prompt?type=working`, {
    headers: getAuthHeaders()
  }, 'working_prompt');
};

export const saveWorkingPrompt = async (prompt) => {
  const res = await fetch(`${API_BASE_URL}/prompt`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ prompt }),
  });
  return handleResponse(res);
};

// USE CASES ENDPOINTS
export const getUseCases = async () => {
  return cachedFetch(`${API_BASE_URL}/usecases`, {
    headers: getAuthHeaders()
  }, 'use_cases_list');
};

export const getUseCaseById = async (id) => {
  return cachedFetch(`${API_BASE_URL}/usecases/${id}`, {
    headers: getAuthHeaders()
  }, `use_case_${id}`);
};

// Update the createUseCase function
export const createUseCase = async (useCaseData) => {
  try {
    // Ensure all required fields are present with defaults
    const completeUseCaseData = {
      name: useCaseData.name || "Neuer Use Case",
      system_prompt: useCaseData.system_prompt,
      task_prompt: useCaseData.task_prompt,
      additional_prompt: useCaseData.additional_prompt,
      comments: useCaseData.comments || ""
    };

    const response = await fetch(`${API_BASE_URL}/v1/usecases/`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(completeUseCaseData)
    });

    const result = await handleResponse(response);
    
    if (result.success) {
      // Invalidate cache
      // requestCache.clear(); // This line was removed as per the new_code, as the cacheInvalidate function is not defined.
      return result.data;
    } else {
      throw new Error(result.message || 'Failed to create use case');
    }
  } catch (error) {
    console.error('Error creating use case:', error);
    throw error;
  }
};

export const updateUseCase = async (id, useCaseData) => {
  try {
    console.log('Updating use case:', id, useCaseData); // Debug log
    
    const response = await fetch(`${API_BASE_URL}/usecases/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(useCaseData),
    });
    
    return handleResponse(response);
  } catch (error) {
    console.error('Error updating use case:', error);
    throw error;
  }
};

export const deleteUseCase = async (id) => {
  try {
    const res = await fetch(`${API_BASE_URL}/usecases/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Delete failed:', res.status, errorText);
      throw new Error(`Delete failed: ${res.status}`);
    }
    
    // Clear cache after deleting
    requestCache.clear();
    return handleResponse(res);
  } catch (error) {
    console.error('Error deleting use case:', error);
    throw error;
  }
};

export const runLLM = async (usecaseId, prompt) => {
  try {
    const response = await fetch(`${API_BASE_URL}/usecases/${usecaseId}/run`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ prompt }),
    });
    return handleResponse(response);
  } catch (error) {
    console.error('Error running LLM:', error);
    throw error;
  }
};

// TAXONOMIE ENDPOINTS  
export const getTaxonomies = async () => {
  try {
    console.log('Making fresh request to:', `${API_BASE_URL}/taxonomie`);
    
    const response = await fetch(`${API_BASE_URL}/taxonomie`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('getTaxonomies fresh response:', result);
    return result;
  } catch (error) {
    console.error('Error in getTaxonomies:', error);
    throw error;
  }
};

// Add new function to get taxonomy by usecase_id
export const getTaxonomyByUseCase = async (usecaseId) => {
  try {
    console.log('Getting taxonomy for usecase:', usecaseId);
    
    // First try the direct endpoint (for backward compatibility)
    try {
      const response = await fetch(`${API_BASE_URL}/taxonomie/usecase/${usecaseId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('getTaxonomyByUseCase response:', result);
        return result;
      }
    } catch (error) {
      console.log('Direct endpoint failed, trying fallback method...');
    }
    
    // Fallback: Get all taxonomies and filter on frontend
    console.log('Using fallback: getting all taxonomies and filtering...');
    const allTaxonomies = await getTaxonomies();
    
    if (Array.isArray(allTaxonomies)) {
      // Find taxonomy that contains this usecase_id in either format
      const matchingTaxonomy = allTaxonomies.find(taxonomy => {
        // Check new format (usecase_ids array)
        if (taxonomy.usecase_ids && Array.isArray(taxonomy.usecase_ids)) {
          return taxonomy.usecase_ids.includes(usecaseId);
        }
        // Check old format (single usecase_id)
        if (taxonomy.usecase_id) {
          return taxonomy.usecase_id === usecaseId;
        }
        return false;
      });
      
      if (matchingTaxonomy) {
        console.log('Found matching taxonomy via fallback:', matchingTaxonomy);
        return matchingTaxonomy;
      }
    }
    
    // No taxonomy found
    console.log('No taxonomy found for usecase:', usecaseId);
    return null;
    
  } catch (error) {
    console.error('Error in getTaxonomyByUseCase:', error);
    return null;
  }
};

// Update the taxonomy functions to handle responses properly

export const createTaxonomy = async (taxonomyData) => {
  try {
    console.log('Creating taxonomy with data:', taxonomyData);
    
    // Clear all cached requests before making the create request
    requestCache.clear();
    
    const res = await fetch(`${API_BASE_URL}/taxonomie`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(taxonomyData),
    });
    
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(errorData.detail || 'Failed to create taxonomy');
    }
    
    const result = await res.json();
    console.log('Create taxonomy result:', result);
    
    // Clear cache again after creating
    requestCache.clear();
    return result;
  } catch (error) {
    console.error('Error in createTaxonomy:', error);
    throw error;
  }
};

export const updateTaxonomy = async (id, taxonomyData) => {
  try {
    console.log('Updating taxonomy:', id, taxonomyData); // Debug log
    
    const response = await fetch(`${API_BASE_URL}/taxonomie/${id}`, {  // Make sure this is PUT with /${id}
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(taxonomyData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Update failed:', response.status, errorText);
      throw new Error(`Update failed: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Update taxonomy result:', result);
    
    // Clear cache after updating
    requestCache.clear();
    return result;
  } catch (error) {
    console.error('Error in updateTaxonomy:', error);
    throw error;
  }
};

export const deleteTaxonomy = async (id) => {
  const res = await fetch(`${API_BASE_URL}/taxonomie/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!res.ok) {
    throw new Error('Failed to delete taxonomy');
  }
  
  return await res.json();
};

export const cloneTaxonomy = async (id) => {
  try {
    console.log('Cloning taxonomy:', id);
    
    const response = await fetch(`${API_BASE_URL}/taxonomie/${id}/clone`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to clone taxonomy' }));
      throw new Error(errorData.detail || 'Failed to clone taxonomy');
    }
    
    const result = await response.json();
    console.log('Clone taxonomy result:', result);
    return result;
    
  } catch (error) {
    console.error('Error in cloneTaxonomy:', error);
    throw error;
  }
};

export const naturalLanguageEditTaxonomy = async (currentTaxonomy, instruction, usecaseIds) => {
  try {
    const response = await fetch(`${API_BASE_URL}/taxonomie/natural-language-edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        current_taxonomy: currentTaxonomy,
        instruction: instruction,
        usecase_ids: usecaseIds
      })
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error in natural language edit:', error);
    throw error;
  }
};

export const fallbackToUC001Taxonomy = async (usecaseIds) => {
  try {
    const response = await fetch(`${API_BASE_URL}/taxonomie/fallback-uc001`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        usecase_ids: usecaseIds
      })
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error in fallback to UC001:', error);
    throw error;
  }
};

export const validateTaxonomyStructure = async (taxonomyData) => {
  try {

    const response = await fetch(`${API_BASE_URL}/taxonomie/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(taxonomyData)
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error in taxonomy validation:', error);
    throw error;
  }
};

// UTILITY ENDPOINTS
export const pingBackend = async () => {
  const res = await fetch(`${API_BASE_URL}/ping`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  return handleResponse(res);
};

// Login function (no auth needed for this)
export const login = async (username, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Login failed');
    }

    const data = await response.json();
    
    if (data.success && data.access_token) {
      // Store both token and expiration timestamp
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('tokenExpiresAt', data.expires_at.toString());
      return data;
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const getCustomerTemplate = async () => {
  try {
    // Return a simple template
    const template = {
      "id": "customer_template",
      "anfragesteller": {
        "vorname": "Maria", 
        "nachname": "Mustermann",
        "email": "maria.mustermann@email.de",
        "anrede": "Frau"
      },
      "patienten": [
        {
          "personendaten": {
            "vorname": "Maria",
            "nachname": "Mustermann", 
            "pflegegrad": { "pflegegrad": "2" }
          },
          "mobilitaet": {
            "hilfsmittel": ["Rollator"],
            "kannGehen": "mit Hilfe"
          }
        }
      ]
    };
    return { success: true, data: template };
  } catch (error) {
    console.error('Error getting customer template:', error);
    return { success: false, error: error.message };
  }
};

export const generateCustomerMail = async (useCaseId, customerData) => {
  try {
    console.log('Sending customer mail request:', {
      use_case_id: useCaseId,
      template_text: "",
      payload: {
        customer_data: customerData
      }
    });

    const response = await fetch(`${API_BASE_URL}/v1/customer_mail/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        use_case_id: useCaseId,
        template_text: "",
        payload: {
          customer_data: customerData
        }
      })
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error generating customer mail:', error);
    throw error;
  }
};

export const getCustomerMailStatus = async (jobId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/v1/customer_mail/${jobId}/`, {
      headers: getAuthHeaders()
    });

    return await handleResponse(response);
  } catch (error) {
    console.error('Error getting customer mail status:', error);
    throw error;
  }
};
